import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getPOReceiptUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import POService from "src/Services/API/po.service";
import {
  DocumentReviewModal,
  DocumentScanningModal,
  getDocumentDisplayName,
} from "src/Pages/WO/Components/documents/DocumentScanModals";

/**
 * Post-upload total-extraction flow for PO receipts, reusing the shared scan
 * modals from the WO proposal/invoice flows. PoDetail's existing receipt UI
 * stays untouched — after its upload finishes it hands the new po_attachment
 * rows here, and each is scanned then reviewed in sequence:
 *
 *   scanning ("Scanning...") -> review (confirm / pick / manual) -> next
 *
 * Closing a review skips that receipt (it stays NEEDS_REVIEW); cancelling the
 * scanning modal aborts the remaining queue. onFinished fires on every exit
 * path so the caller can refresh.
 */
function PoReceiptScanFlow({ receipts, onFinished }) {
  const [phase, setPhase] = useState("scanning"); // scanning | review
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null); // { document, result }
  const [busy, setBusy] = useState(false);
  const totalRef = useRef(receipts?.length || 0);
  // Bumped when the flow is cancelled so late scan responses are ignored.
  const flowIdRef = useRef(0);

  const finish = () => {
    flowIdRef.current++;
    onFinished?.();
  };

  const startScan = async (document, remaining) => {
    const flowId = flowIdRef.current;
    setQueue(remaining);
    setCurrent({ document, result: null });
    setPhase("scanning");
    try {
      const res = await POService.scanReceipt(document.id);
      if (flowIdRef.current !== flowId) return; // flow was cancelled
      setCurrent({ document: res.data?.document || document, result: res.data });
      setPhase("review");
    } catch (error) {
      if (flowIdRef.current !== flowId) return;
      // Scan failure = nothing extracted; offer manual entry / retry.
      setCurrent({ document, result: { status: "NOT_FOUND", best: null, candidates: [] } });
      setPhase("review");
    }
  };

  const advance = () => {
    if (queue.length) {
      const [next, ...rest] = queue;
      startScan(next, rest);
    } else {
      finish();
    }
  };

  const handleConfirm = async (amount, sourcePage, sourceLabel) => {
    if (!current?.document) return;
    setBusy(true);
    try {
      await POService.confirmReceiptAmount(current.document.id, amount, sourcePage, sourceLabel);
      toast("Receipt amount confirmed");
      advance();
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  // Kick off the queue once for the receipts this instance was mounted with.
  useEffect(() => {
    if (!receipts?.length) {
      onFinished?.();
      return undefined;
    }
    const [first, ...rest] = receipts;
    startScan(first, rest);
    return () => {
      flowIdRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!current) return null;

  if (phase === "scanning") {
    const index = totalRef.current - queue.length;
    const label =
      totalRef.current > 1
        ? `${getDocumentDisplayName(current.document)} (file ${index} of ${totalRef.current})`
        : getDocumentDisplayName(current.document);
    return <DocumentScanningModal noun="Receipt" fileLabel={label} onCancel={finish} />;
  }

  if (phase === "review" && current.result) {
    return (
      <DocumentReviewModal
        document={current.document}
        noun="Receipt"
        totalLabel="Purchase Order's total receipted amount"
        getFileUrl={(receipt) => getPOReceiptUrl(receipt.fileName)}
        result={current.result}
        busy={busy}
        onConfirm={handleConfirm}
        onClose={advance}
        onRescan={() => startScan(current.document, queue)}
      />
    );
  }

  return null;
}

export default PoReceiptScanFlow;
